import * as RadixDialog from "@radix-ui/react-dialog";
import { ArrowLeft, X } from "lucide-react";
import { type ReactNode, useState } from "react";
import { Icon } from "../icons/Icon";
import { cx } from "../styles";
import { ConfirmDialog } from "./Dialog";
import { CLOSE_BUTTON, PANEL, SCRIM, TRANSITION } from "./overlay";
import { useUiText } from "./uiText";

/**
 * A panel on the inline-end edge — and a full page on a phone.
 *
 * The drawer is where a form with more than a few fields lives: it can scroll
 * its body independently of a footer that stays put, so Save never scrolls out
 * of reach, and it keeps the list behind it visible so somebody editing a row
 * can still see where it sits.
 *
 * **Inline-end, not right.** In Arabic the panel is anchored to the visual
 * *left* edge and the close button sits at the header's left, because the
 * inline end is where a reader's eye finishes. Every offset here is logical for
 * that reason; a `right: 0` would put the panel on the wrong side for half the
 * people using it, and it would look perfectly correct to whoever wrote it.
 *
 * **Full page under 640px.** A 440px panel on a 375px screen is the whole
 * screen with a sliver of scrim nobody can hit, so below the breakpoint it
 * becomes a page and the close button becomes a back arrow — which is the
 * gesture a phone user already has.
 *
 * **Escape asks when there is unsaved work.** `dirty` turns the close into a
 * confirmation rather than an action. Losing a half-filled form to a stray
 * Escape is the single most annoying thing an overlay can do, and Escape is
 * pressed by reflex.
 */

export type DrawerSize = "sm" | "md" | "lg";

const SIZES: Record<DrawerSize, string> = {
	sm: "sm:w-[440px]",
	md: "sm:w-[560px]",
	lg: "sm:w-[720px]",
};

export interface DrawerProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Names the drawer; it is what `aria-labelledby` points at. */
	title: ReactNode;
	/** A line under the title. */
	description?: ReactNode;
	size?: DrawerSize;
	/** The scrolling body. */
	children: ReactNode;
	/** Actions, end-aligned and fixed below the body. */
	footer?: ReactNode;
	/** Unsaved work: closing asks first. */
	dirty?: boolean;
	closeLabel?: string;
	backLabel?: string;
	/**
	 * What it becomes under 640px: a full `page` (the default, for forms), or
	 * a bottom `sheet` for a short list somebody picks from — filters, a
	 * selection's actions (ADJ-M03-3) — which keeps the list behind in view.
	 */
	phone?: "page" | "sheet";
	/** The confirmation shown when a dirty drawer is closed. */
	discard?: {
		title: ReactNode;
		description?: ReactNode;
		confirmLabel: string;
		cancelLabel: string;
	};
}

export function Drawer({
	open,
	onOpenChange,
	title,
	description,
	size = "sm",
	children,
	footer,
	dirty = false,
	closeLabel: closeGiven,
	backLabel: backGiven,
	discard,
	phone = "page",
}: DrawerProps) {
	// Named in the locale when the caller gives no label (ADJ-M03-12).
	const t = useUiText();
	const closeLabel = closeGiven ?? t("ui.actions.close");
	const backLabel = backGiven ?? t("ui.actions.back");
	const [asking, setAsking] = useState(false);
	const sheet = phone === "sheet";

	/**
	 * One gate for every way out — Escape, the scrim, the close button.
	 *
	 * Radix routes all three through `onOpenChange`, which is what makes this
	 * possible to write once. Guarding only the close button would leave Escape
	 * as a way to lose the form, and Escape is the one people press.
	 */
	function requestClose(next: boolean) {
		if (next) {
			onOpenChange(true);
			return;
		}
		if (dirty && discard !== undefined) {
			setAsking(true);
			return;
		}
		onOpenChange(false);
	}

	return (
		<>
			<RadixDialog.Root open={open} onOpenChange={requestClose}>
				<RadixDialog.Portal>
					<RadixDialog.Overlay className={cx(SCRIM, TRANSITION)} />
					<RadixDialog.Content
						className={cx(
							PANEL,
							// Pinned to the inline end and both block edges: the panel is
							// as tall as the viewport and as wide as its size says.
							"flex w-full flex-col",
							sheet
								? // A sheet rises from the bottom edge on a phone and is the
									// same side panel from 640px up.
									"max-sm:inset-x-0 max-sm:bottom-0 max-sm:max-h-[85dvh] max-sm:rounded-t-xl max-sm:border-t max-sm:border-border sm:inset-y-0 sm:end-0 max-sm:data-[state=open]:animate-[km-rise_150ms_ease-out]"
								: "inset-y-0 end-0",
							"sm:border-s sm:border-border",
							SIZES[size],
							// Slides from the edge it is anchored to, which is a different
							// edge in Arabic.
							sheet
								? "sm:data-[state=open]:animate-[km-slide-from-end_150ms_ease-out] sm:rtl:data-[state=open]:animate-[km-slide-from-end-rtl_150ms_ease-out]"
								: "data-[state=open]:animate-[km-slide-from-end_150ms_ease-out] rtl:data-[state=open]:animate-[km-slide-from-end-rtl_150ms_ease-out]",
							TRANSITION,
						)}
					>
						<div className="flex items-center gap-3 border-b border-border px-5 py-4">
							{/* On a phone this is a page, so the way out is a back arrow —
							    and the arrow mirrors, because back is the direction the
							    text came from. A sheet keeps its ×. */}
							{!sheet && (
								<RadixDialog.Close aria-label={backLabel} className={cx(CLOSE_BUTTON, "sm:hidden")}>
									<Icon as={ArrowLeft} size={18} mirror />
								</RadixDialog.Close>
							)}

							<div className="min-w-0 flex-1">
								<RadixDialog.Title className="truncate text-[16px] font-bold tracking-[-0.01em] text-fg">
									{title}
								</RadixDialog.Title>
								{description !== undefined && (
									<RadixDialog.Description className="mt-1 text-[12.5px] leading-relaxed text-fg-muted">
										{description}
									</RadixDialog.Description>
								)}
							</div>

							<RadixDialog.Close
								aria-label={closeLabel}
								className={cx(CLOSE_BUTTON, !sheet && "max-sm:hidden")}
							>
								<Icon as={X} size={18} />
							</RadixDialog.Close>
						</div>

						{/* The body scrolls; the footer does not. That is the whole reason
						    a long form belongs in a drawer rather than a dialog. */}
						<div
							data-scroll="1"
							className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4 text-[13px] text-fg"
						>
							{children}
						</div>

						{footer !== undefined && (
							<div
								// What the toast stack rises above (index.css, ADJ-1016-8).
								data-drawer-footer=""
								className="flex justify-end gap-2 border-t border-border bg-bg-subtle px-5 py-3.5"
							>
								{footer}
							</div>
						)}
					</RadixDialog.Content>
				</RadixDialog.Portal>
			</RadixDialog.Root>

			{discard !== undefined && (
				<ConfirmDialog
					open={asking}
					onOpenChange={setAsking}
					title={discard.title}
					{...(discard.description === undefined ? {} : { description: discard.description })}
					tone="danger"
					confirmLabel={discard.confirmLabel}
					cancelLabel={discard.cancelLabel}
					onConfirm={() => {
						setAsking(false);
						onOpenChange(false);
					}}
				/>
			)}
		</>
	);
}
